import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { Platform, View } from 'react-native';
import apis from '@api/api';
import { useSelector, useDispatch } from 'react-redux';
import * as Animatable from 'react-native-animatable';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { useQuery } from '@tanstack/react-query';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import BottomSheet, {
  BottomSheetModalProvider,
} from '@gorhom/bottom-sheet';
import AnimatedLottieView from '@utils/lottieAnimation';
import RobotHelper from '@components/RobotHelper/RobotHelper';
import { NavigationHelpers, useFocusEffect } from '@react-navigation/native';
import { CLEAR_ACTION_HANDLER_HISTORY, CLEAR_UPLOADER_HISTORY, HIDE_TOOLBAR, SET_ACTIONS, SHOW_TOOLBAR } from '@reduxLocal/actions/types';
import { FontAwesome5 } from '@expo/vector-icons';
import { DefaultRootState } from '@reduxLocal/persistState';
import { ReactNativeZoomableView } from '@openspacelabs/react-native-zoomable-view';
import FlowHierarchy from '../Components/FlowHierarchy';
import getGlobalUser from '@hooks/getGlobalUser';
import { NativeStackNavigationHelpers, NativeStackNavigationProp } from '@react-navigation/native-stack/lib/typescript/src/types';
import ErrorBoundary from '@utils/ErrorBoundary';

type ActionHandlerTypes = {
  navigation: NativeStackNavigationProp<Record<string, object>, any>,
}


const ActionHandlerScreen = ({ navigation }: ActionHandlerTypes) => {
  const id = navigation.getState().routes[navigation.getState().index].params?.dataId;
  const toolbarDisplayed = useSelector((state: DefaultRootState) => state.modals.toolbar.toolbarDisplayed);
  const zoomableViewRef = useRef<ReactNativeZoomableView>(null);
  const dispatch = useDispatch();
  const theme = themeSelector();
  const [listVisible, setListVisible] = useState(false);
  const [userList, setUserList] = useState([{}]);
  const [loaded, setLoaded] = useState(false);
  const addUserRef = useRef<AddUserModalHandle>(null);
  const [helpState, setHelpState] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const userId = getGlobalUser();
  const helpText = ['Create powerful custom actions for your data', 'You can add an action on data submission by using the arrow at the bottom', 'You can also link fields with other data that you have stored in SmartSaaS to create links between the data']


  const dispatchUserToolbar = () => {
    dispatch({
      type: SHOW_TOOLBAR,
      payload: {
        draggable: false,
        historyTracked: false,
        component: 'user',
        refId: 'userRefId',
        items: [
          { id: "history", toolName: "History", icon: "history", stateName: "actionMaker", description: "View the history of the user", action: "history" },
          { id: 'zoomIn', toolName: 'Zoom In', icon: 'plus-square', description: 'Zoom in on the map', action: "zoomIn" },
          { id: 'zoomOut', toolName: 'Zoom Out', icon: 'minus-square', description: 'Zoom out on the map', action: "zoomOut" },
          { id: 'centerView', toolName: 'Center', icon: 'plus-circle', description: 'Center the view on the map', action: "centerView" },
          { id: 'help', toolName: 'Help', icon: 'question-circle', description: 'Open the help menu', action: "help" },
        ]
      }
    });
    setLoaded(true)
  };


  const _handleReset = useCallback(() => {
    setLoaded(false);
    setTimeout(() => {
      setLoaded(true);
    }, 1000);
  }, [loaded, userList])

  function _handleSnap(value) {
    console.log('trying to snap', value);
    if (value && value !== "" && value !== null && value !== undefined) {
      setSelectedUser(value);
      // userOptionsRef.current?.openModal(value);
      // const userProfile = userList.find((user) => user.user._id === value);
      // console.log(userProfile);
      // try {
      //   if (userOptionsRef.current) {
      //     userOptionsRef.current.openModal(userProfile);
      //   }
      // } catch (e) {
      //   console.log(e);
      // }
    }
  }

  const { status, data, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['schemaActions'],
    queryFn: async () => {
      const data = await apis.getDataSchema(userId, id)
        .then((res) => {
          console.log(res.data.data, 'schema data');
          return res.data.data;
        })
      if (data?.actions?.length) {
        dispatch({ type: SET_ACTIONS, payload: data.actions })
      } else {
        dispatch({ type: SET_ACTIONS, payload: [] })
      }
      return data;
    },
    staleTime: Infinity,
  });

  useFocusEffect(
    useCallback(() => {
      const beforeRemoveListener = () => {
        // implement function to save changes
        dispatch({ type: CLEAR_ACTION_HANDLER_HISTORY });

      };
      if (!loaded) {
        dispatchUserToolbar();
      }
      // Clean up the listener when the component loses focus
      return () => { dispatch({ type: HIDE_TOOLBAR }); dispatch({ type: CLEAR_ACTION_HANDLER_HISTORY }); navigation.removeListener('beforeRemove', beforeRemoveListener) };
    }, [navigation])
  );

  const _openAddUserModal = useCallback(() => {
    addUserRef.current?.openModal();
  }, [addUserRef]);


  return (
    <DefaultScreen scrollDisabled backButtonStyle={{top: -10}} style={{paddingTop: 0}}>
      <View style={{ flex: 1, marginRight: -20 }}>
        <Animatable.View useNativeDriver transition={'flex'} style={{ flex: listVisible ? 0.85 : 1 }} direction={listVisible ? 'normal' : 'reverse'}>
          <View style={{ flex: 7, alignSelf: 'stretch' }}>
            {
              !toolbarDisplayed &&
              <Animatable.View useNativeDriver style={{ position: 'absolute', top: Platform.select({ web: 350, default: "14%" }), right: Platform.select({ web: 400, default: "28%" }), maxWidth: 60, maxHeight: 60, zIndex: 10, shadowRadius: 10, shadowOpacity: 0.4, overflow: 'visible', shadowColor: 'rgb(90,192,234)', borderRadius: 20, backgroundColor: 'rgb(90,192,234)' }} animation={'slideInRight'}>
                <FontAwesome5 onPress={() => dispatchUserToolbar()} name="tools" style={{ textAlign: 'center', backgroundColor: theme ? 'white' : 'black', padding: 10, borderRadius: 10, shadowRadius: 20, shadowColor: 'rgb(90,192,234)', overflow: 'hidden' }} size={24} color={'rgb(90,192,234)'} />
              </Animatable.View>
            }
            <ErrorBoundary>
            <FlowHierarchy isLoading={isLoading} ref={zoomableViewRef} schemaData={data} openAddUserModal={_openAddUserModal} handleSnap={_handleSnap} />
            </ErrorBoundary>
          </View>
          {
            helpState &&
            <RobotHelper style={{ marginHorizontal: '20%', bottom: '50%', maxHeight: 200, minWidth: Platform.OS === 'web' ? 400 : 200 }} helpText={helpText} setState={setHelpState} />
          }
        </Animatable.View>
      </View>
    </DefaultScreen >
  );
}

export default ActionHandlerScreen;
